import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table, Button, AutoComplete, Input, Modal, Popconfirm, Select, Checkbox, Typography } from "antd";
import { getCacheOptions, updateCacheOptions } from "../../../firebase/cache/create";
import { PlusOutlined, CloseOutlined, EditOutlined, SplitCellsOutlined } from "@ant-design/icons";
import AutoCompleteModal from "./AutoCompleteModal";
import store from "../../../redux/store";
import { totalEmissions } from "../../../components/text/resultEmissions";
import moment from "moment";
import { getDivideByOptions } from "../../../helper/attribute";
import Visual from "../../../components/Visual";
import { getFormSortingKey } from "../../../static/formRoutes";
import { getGHGTitle } from "../../../static/ghg.static";

const { TextArea } = Input;


const memoTotalEmissions = {

}



const defaultConfig = {
  verifier: {
    suggestions: [
      "Evidence provided for data entered, with actual figures used.",
      "Evidence provided for data entered, with estimated figures used.",
      "No data available, estimated based on industry standards.",
      "Source provided for data, no evidence available.",
      "Estimated, based on typical business operation.",
      "Estimated.",
    ],
  },
  source: {
    suggestions: ["Test"],
  },
};

// Function to get total tCO2e based on form name with date filtering
const getTotalEmissions = (record, startDate, endDate, filterer = null) => {
  // Use formName if available (new structure), otherwise fall back to data field

  if (!record) return 0;
  const formName = record.formName;

  const key = JSON.stringify({ formName, startDate, endDate, filterer });
  if (memoTotalEmissions[key]) {
    return memoTotalEmissions[key];
  }

  let results = store.getState().app.results.filter(result => result.name === formName);

  // Filter results by date range if dates are provided
  if (startDate && endDate) {
    const startMoment = moment(startDate, "DD/MM/YYYY");
    const endMoment = moment(endDate, "DD/MM/YYYY");

    results = results.filter(result => {
      if (!result.date) return false;
      const resultMoment = moment(result.date, "DD/MM/YYYY");
      return resultMoment.isBetween(startMoment, endMoment, 'day', '[]'); // inclusive of both dates
    });
  }

  // Apply additional filtering if filterer is provided (divide by property)
  if (filterer && filterer.property && filterer.value) {
    results = results.filter(result => {
      return result[filterer.property] === filterer.value;
    });
  }
  // console.log(filterer, "filterer", results, "results", formName, "formName")
  const emissions = totalEmissions(results, true);
  if(results.length > 0&& formName === "water"){
    console.log("Emissions", results, formName, emissions.total_emissions)
  }
  memoTotalEmissions[key] = emissions.total_emissions;// to be used for sorter
  return emissions.total_emissions;
};

// Function to calculate percentage
const calculatePercentage = (currentEmissions, totalEmissions) => {
  if (!totalEmissions || totalEmissions === 0) return 0;
  return currentEmissions / totalEmissions;
};

// Function to determine materiality based on percentage
const getMateriality = (percentage) => {
  if (percentage > 0.4) return "Very High (>40%)";
  if (percentage > 0.2) return "High (20-40%)";
  if (percentage > 0.05) return "Medium (5-20%)";
  if (percentage > 0.01) return "Low (1-5%)";
  if (percentage < 0.01) return "Very Low (<1%)";
  return "n/a";
};

// Function to calculate uncertainty based on accuracy
const getUncertainty = (accuracy, options) => {
  const option = options.find(opt => opt.value === accuracy);
  return option ? option.uncertainty : "";
};

// Function to calculate error margin
const getErrorMargin = (emissions, uncertainty) => {
  if (uncertainty === "" || !emissions) return "";
  return emissions * uncertainty;
};

const SimpleEditableTable = React.memo(({ dataSource, setDataSource, year, startDate, endDate, onCalculatedDataChange }) => {

  const [tempDataSource, setTempDataSource] = useState(dataSource || []); // Temporary data source
  const [isModalVisible, setIsModalVisible] = useState(false); // Modal state
  const [newOption, setNewOption] = useState(""); // New option value
  const [columnToUpdate, setColumnToUpdate] = useState(""); // Column to update (source/verifier)
  const [selectedDataField, setSelectedDataField] = useState(""); // To store the current data field for adding new options
  const [optionsCache, setOptionsCache] = useState({}); // Cache for loaded options

  // Accuracy options state with localStorage
  const [accuracyOptions, setAccuracyOptions] = useState(() => {
    const saved = localStorage.getItem('accuracyOptions');
    return saved ? JSON.parse(saved) : [
      { label: "n/a", value: "n/a", uncertainty: "" },
      { label: "Excellent", value: "Excellent", uncertainty: 0.01 },
      { label: "Very Good", value: "Very Good", uncertainty: 0.05 },
      { label: "Good", value: "Good", uncertainty: 0.1 },
      { label: "Average", value: "Average", uncertainty: 0.5 },
      { label: "Poor", value: "Poor", uncertainty: 0.9 },
      { label: "Very Poor", value: "Very Poor", uncertainty: 1 }
    ];
  });



  // AutoComplete Modal state
  const [autoCompleteModalVisible, setAutoCompleteModalVisible] = useState(false);
  const [currentEditingField, setCurrentEditingField] = useState(null);
  const [currentEditingValue, setCurrentEditingValue] = useState("");
  const [currentEditingColumn, setCurrentEditingColumn] = useState("");

  // Divider Modal state
  const [dividerModalVisible, setDividerModalVisible] = useState(false);
  const [currentDividerRecord, setCurrentDividerRecord] = useState(null);
  const [availableProperties, setAvailableProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [dividedRecords, setDividedRecords] = useState({});

  // State to store calculated table data for Excel export
  const [calculatedTableData, setCalculatedTableData] = useState([]);

  const [filters, setFilters] = useState({
    hideZeroEmissions: true,
  });

  // console.log("selectedProperty", selectedProperty)

  useEffect(() => {
    if (JSON.stringify(tempDataSource) !== JSON.stringify(dataSource)) {
      setDataSource(tempDataSource);
    }
  }, [tempDataSource, setDataSource]);

  // console.log("Datasource", dataSource)

  // Calculate overall total emissions for percentage calculations
  const overallTotalEmissions = useMemo(() => {
    return tempDataSource.reduce((total, record) => {
      const emissions = getTotalEmissions(record, startDate, endDate, record.filterer);
      return total + emissions;
    }, 0);
  }, [tempDataSource, startDate, endDate]);

  // Calculate total emissions for all rows
  const totalEmissionsForAllRows = useMemo(() => {
    return tempDataSource.reduce((total, record) => {
      const emissions = getTotalEmissions(record, startDate, endDate, record.filterer);
      return total + emissions;
    }, 0);
  }, [tempDataSource, startDate, endDate]);

  // Calculate total error margin
  const totalErrorMargin = useMemo(() => {
    return tempDataSource.reduce((total, record) => {
      const emissions = getTotalEmissions(record, startDate, endDate, record.filterer);
      const accuracy = record.accuracy || "n/a";
      const uncertainty = getUncertainty(accuracy, accuracyOptions);
      const errorMargin = getErrorMargin(emissions, uncertainty);
      return total + (errorMargin || 0);
    }, 0);
  }, [tempDataSource, startDate, endDate]);

  // Calculate and store complete table data with all computed values
  const computedTableData = useMemo(() => {
    const dataWithCalculations = tempDataSource.map(record => {
      const totalEmissions = getTotalEmissions(record, startDate, endDate, record.filterer);
      const percentage = calculatePercentage(totalEmissions, overallTotalEmissions);
      const materiality = getMateriality(percentage);
      const accuracy = record.accuracy || "n/a";
      const uncertainty = getUncertainty(accuracy, accuracyOptions);
      const errorMargin = getErrorMargin(totalEmissions, uncertainty);

      return {
        ...record,
        // Use actual column headers as keys with string values
        "Data": record.data || "",
        "Source of data": record.source || "",
        "Verifier comments": record.verifier || "",
        "Total tCO₂e": totalEmissions?.toFixed(2) || "0.00",
        "Accuracy": accuracy,
        "Percentage": `${(percentage * 100)?.toFixed(2)}%`,
        "Materiality": materiality,
        "Uncertainty": uncertainty === "" ? "" : `${(uncertainty * 100)?.toFixed(2)}%`,
        "Error Margin": errorMargin === "" ? "" : errorMargin?.toFixed(2),
        // Keep calculated values for internal use
        calculatedTotalEmissions: totalEmissions,
        calculatedPercentage: percentage,
        calculatedPercentageDisplay: `${(percentage * 100)?.toFixed(2)}%`,
        calculatedMateriality: materiality,
        calculatedUncertainty: uncertainty,
        calculatedUncertaintyDisplay: uncertainty === "" ? "" : `${(uncertainty * 100)?.toFixed(2)}%`,
        calculatedErrorMargin: errorMargin,
        calculatedErrorMarginDisplay: errorMargin === "" ? "" : errorMargin?.toFixed(2)
      };
    });

    // Add totals row
    const totalsRow = {
      key: 'totals',
      data: 'Total Emissions',
      source: '',
      verifier: '',
      accuracy: '',
      // Use actual column headers as keys with string values
      "Data": "Total Emissions",
      "Source of data": "",
      "Verifier comments": "",
      "Total tCO₂e": totalEmissionsForAllRows?.toFixed(2) || "0.00",
      "Accuracy": "",
      "Percentage": "100.00%",
      "Materiality": "",
      "Uncertainty": "",
      "Error Margin": totalErrorMargin?.toFixed(2) || "0.00",
      // Keep calculated values for internal use
      calculatedTotalEmissions: totalEmissionsForAllRows,
      calculatedPercentage: 1,
      calculatedPercentageDisplay: '100.00%',
      calculatedMateriality: '',
      calculatedUncertainty: '',
      calculatedUncertaintyDisplay: '',
      calculatedErrorMargin: totalErrorMargin,
      calculatedErrorMarginDisplay: totalErrorMargin?.toFixed(2),
      isTotalRow: true
    };

    return [...dataWithCalculations, totalsRow];
  }, [tempDataSource, startDate, endDate, overallTotalEmissions, totalEmissionsForAllRows, totalErrorMargin]);

  // Update the calculated table data state whenever computed data changes
  useEffect(() => {
    // Store the filtered and sorted data for Excel export
    const filteredData = computedTableData.filter(r => {
      if (r.isTotalRow) return true; // Always include totals row
      if (filters.hideZeroEmissions) {
        return r.calculatedTotalEmissions > 0;
      }
      return true;
    });

    const sortedData = filteredData.sort((a, b) => {
      // Keep totals last
      if (a.isTotalRow) return 1;
      if (b.isTotalRow) return -1;

      // Extract parent names (everything before the first " - ")
      const aParent = a.data?.split(" - ")[0] || "";
      const bParent = b.data?.split(" - ")[0] || "";

      // First sort by parent name (alphabetically)
      if (aParent !== bParent) {
        // But prioritize by parent's total emissions first
        const aParentTotal = filteredData
          .filter(item => !item.isTotalRow && item.data?.split(" - ")[0] === aParent)
          .reduce((sum, item) => sum + (item.calculatedTotalEmissions || 0), 0);
        
        const bParentTotal = filteredData
          .filter(item => !item.isTotalRow && item.data?.split(" - ")[0] === bParent)
          .reduce((sum, item) => sum + (item.calculatedTotalEmissions || 0), 0);
        
        return bParentTotal - aParentTotal; // Higher emissions first
      }

      // Within the same parent group, sort by individual emissions (highest first)
      const aTotal = a.calculatedTotalEmissions || 0;
      const bTotal = b.calculatedTotalEmissions || 0;
      return bTotal - aTotal;
    });

    setCalculatedTableData(sortedData);
    // Notify parent component about the calculated data changes
    // console.log("Calculated Table Data", sortedData)
    if (onCalculatedDataChange) {
      onCalculatedDataChange(sortedData);
    }
  }, [computedTableData, filters.hideZeroEmissions, onCalculatedDataChange]);


  // Function to load options for a specific data field from Firebase, with fallback to defaultConfig
  const loadOptionsForField = useCallback(async (dataField) => {
    if (!optionsCache[`${dataField}_commenttable`]) {
      try {
        const cacheOptions = await getCacheOptions(dataField); // Fetch options from Firebase
        setOptionsCache((prevCache) => ({
          ...prevCache,
          [`${dataField}_commenttable`]: cacheOptions || defaultConfig,
        }));
        return cacheOptions || defaultConfig;
      } catch (error) {
        console.error(`Error fetching options for ${dataField}:`, error);
        return defaultConfig; // Return default config if fetching fails
      }
    }
    return optionsCache[`${dataField}_commenttable`] || defaultConfig;
  }, [optionsCache]);

  // Use effect to preload options when component mounts for the first time
  useEffect(() => {
    async function preloadOptions() {
      const dataFields = tempDataSource.map((record) => record.formName || record.data);
      for (const dataField of dataFields) {
        await loadOptionsForField(dataField);
      }
    }
    preloadOptions();
  }, [tempDataSource, loadOptionsForField]);

  // Handle input changes locally in tempDataSource
  const handleInputChange = useCallback((value, recordKey, column) => {
    setTempDataSource((prevData) =>
      prevData.map((item) =>
        item.key === recordKey ? { ...item, [column]: value } : item
      )
    );
  }, []);

  // Handle adding new option to the correct field and column
  const handleAddNewOption = useCallback(async () => {
    if (newOption && columnToUpdate && selectedDataField) {
      const currentOptions = await loadOptionsForField(selectedDataField);
      const updatedOptions = {
        ...currentOptions,
        [columnToUpdate]: {
          ...currentOptions[columnToUpdate],
          suggestions: [
            ...(currentOptions[columnToUpdate]?.suggestions || []),
            newOption,
          ],
        },
      };
      await updateCacheOptions(selectedDataField, updatedOptions); // Save updated options to Firebase
      setOptionsCache((prevCache) => ({
        ...prevCache,
        [`${selectedDataField}_commenttable`]: updatedOptions,
      }));
      setIsModalVisible(false); // Close modal
      setNewOption(""); // Reset new option input
    }
  }, [newOption, columnToUpdate, selectedDataField, loadOptionsForField]);

  // Handle removing an option from suggestions
  const handleRemoveOption = useCallback(async (optionToRemove, column, dataField) => {
    const currentOptions = await loadOptionsForField(dataField);
    const updatedSuggestions = currentOptions[column]?.suggestions?.filter(
      (suggestion) => suggestion !== optionToRemove
    ) || [];

    const updatedOptions = {
      ...currentOptions,
      [column]: {
        ...currentOptions[column],
        suggestions: updatedSuggestions,
      },
    };

    await updateCacheOptions(dataField, updatedOptions);
    setOptionsCache((prevCache) => ({
      ...prevCache,
      [`${dataField}_commenttable`]: updatedOptions,
    }));
  }, [loadOptionsForField]);

  // Show modal for adding a new option
  const showAddOptionModal = useCallback((column, dataField) => {
    setColumnToUpdate(column);
    setSelectedDataField(dataField);
    setIsModalVisible(true); // Open modal
  }, []);

  // Handle opening autocomplete modal
  const openAutoCompleteModal = useCallback((record, column) => {
    setCurrentEditingField(record);
    setCurrentEditingColumn(column);
    setCurrentEditingValue(record[column] || "");
    setAutoCompleteModalVisible(true);
  }, []);

  // Handle saving from autocomplete modal
  const handleAutoCompleteSave = useCallback((value) => {
    if (currentEditingField && currentEditingColumn) {
      handleInputChange(value, currentEditingField.key, currentEditingColumn);
    }
    setAutoCompleteModalVisible(false);
    setCurrentEditingField(null);
    setCurrentEditingValue("");
    setCurrentEditingColumn("");
  }, [currentEditingField, currentEditingColumn, handleInputChange]);

  // Handle adding suggestion from autocomplete modal
  const handleAddSuggestionFromModal = useCallback(async (suggestion, fieldKey) => {
    if (currentEditingField && currentEditingColumn) {
      const fieldName = currentEditingField.formName || currentEditingField.data;
      const currentOptions = await loadOptionsForField(fieldName);
      const updatedOptions = {
        ...currentOptions,
        [currentEditingColumn]: {
          ...currentOptions[currentEditingColumn],
          suggestions: [
            ...(currentOptions[currentEditingColumn]?.suggestions || []),
            suggestion,
          ],
        },
      };
      await updateCacheOptions(fieldName, updatedOptions);
      setOptionsCache((prevCache) => ({
        ...prevCache,
        [`${fieldName}_commenttable`]: updatedOptions,
      }));
    }
  }, [currentEditingField, currentEditingColumn, loadOptionsForField]);

  // Handle removing suggestion from autocomplete modal
  const handleRemoveSuggestionFromModal = useCallback(async (suggestion, fieldKey) => {
    if (currentEditingField && currentEditingColumn) {
      const fieldName = currentEditingField.formName || currentEditingField.data;
      const currentOptions = await loadOptionsForField(fieldName);
      const updatedSuggestions = currentOptions[currentEditingColumn]?.suggestions?.filter(
        (s) => s !== suggestion
      ) || [];

      const updatedOptions = {
        ...currentOptions,
        [currentEditingColumn]: {
          ...currentOptions[currentEditingColumn],
          suggestions: updatedSuggestions,
        },
      };

      await updateCacheOptions(fieldName, updatedOptions);
      setOptionsCache((prevCache) => ({
        ...prevCache,
        [`${fieldName}_commenttable`]: updatedOptions,
      }));
    }
  }, [currentEditingField, currentEditingColumn, loadOptionsForField]);

  // Handle adding new accuracy option
  const handleAddAccuracyOption = useCallback(() => {
    const label = window.prompt("Enter accuracy label:");
    if (!label) return;
    
    const uncertaintyPercent = window.prompt("Enter uncertainty percentage (e.g., 15 for 15%):");
    if (!uncertaintyPercent) return;
    
    const uncertainty = parseFloat(uncertaintyPercent) / 100;
    if (isNaN(uncertainty)) {
      alert("Please enter a valid number for uncertainty percentage");
      return;
    }
    
    const newOptions = [...accuracyOptions, {
      label: label,
      value: label,
      uncertainty: uncertainty
    }];
    setAccuracyOptions(newOptions);
    localStorage.setItem('accuracyOptions', JSON.stringify(newOptions));
  }, []);

  // Custom filter function for AutoComplete
  const filterOption = useCallback((inputValue, option) =>
    option.value.toLowerCase().includes(inputValue.toLowerCase())
    , []);

  // Custom dropdown render with remove functionality
  const renderDropdown = useCallback((menu, column, dataField) => {
    const currentOptions = optionsCache[`${dataField}_commenttable`] || defaultConfig;
    const suggestions = currentOptions[column]?.suggestions || [];

    return (
      <div style={{ minWidth: "200px" }}>
        {suggestions.length > 0 ? (
          suggestions.map((suggestion, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                cursor: 'pointer',
                borderBottom: index < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                transition: 'background-color 0.2s',
              }}
              className="suggestion-item"
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              onClick={() => {
                // Find the record and update it
                const record = tempDataSource.find(item => (item.formName || item.data) === dataField);
                if (record) {
                  handleInputChange(suggestion, record.key, column);
                }
              }}
            >
              <span style={{ flex: 1, marginRight: '12px', fontSize: '14px' }}>{suggestion}</span>
              <Popconfirm
                title="Remove this suggestion?"
                onConfirm={(e) => {
                  e.stopPropagation();
                  handleRemoveOption(suggestion, column, dataField);
                }}
                okText="Yes"
                cancelText="No"
              >
                <CloseOutlined
                  style={{
                    color: '#ff4d4f',
                    fontSize: '14px',
                    padding: '6px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s'
                  }}
                  className="remove-icon"
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#fff2f0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  onClick={(e) => e.stopPropagation()}
                />
              </Popconfirm>
            </div>
          ))
        ) : (
          <div style={{
            padding: '16px',
            textAlign: 'center',
            color: '#999',
            fontSize: '14px'
          }}>
            No suggestions available
          </div>
        )}
        <div style={{ borderTop: '1px solid #f0f0f0', marginTop: suggestions.length > 0 ? '4px' : '0' }}>
          <Button
            type="link"
            style={{
              display: 'flex',
              width: "100%",
              justifyContent: 'center',
              alignItems: 'center',
              padding: '16px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              color: '#1890ff'
            }}
            onClick={() => showAddOptionModal(column, dataField)}
          >
            <PlusOutlined style={{ marginRight: '8px' }} /> Add new option
          </Button>
        </div>
      </div>
    );
  }, [optionsCache, tempDataSource, handleInputChange, handleRemoveOption, showAddOptionModal]);

  // Table columns with autocomplete options
  const columns = useMemo(() => [
    {
      title: "Data",
      dataIndex: "data",
      key: "data",
      width: 80,
      fixed: 'left',
      render: (text, record) => {
        // let's consider a number property after the first - is going to be ghg only

        const ghgValue = text.split(" - ").length > 1 && text.split(" - ")[1].match(/^\d+$/) ? text.split(" - ")[1] : null;

        if (ghgValue) {
          return <span style={{ fontStyle: 'italic', fontSize: '12px', color: '#666', fontWeight: 'normal' }}>{text.split(" - ")[0] + " - " + getGHGTitle(ghgValue)}</span>;
        }
        if (record.isTotalRow) {
          return <span style={{ fontStyle: 'italic', fontSize: '12px', color: '#666', fontWeight: 'normal' }}>{text}</span>;
        }
        if (record.key === 'instructions') {
          return <span style={{ fontStyle: 'italic', fontSize: '12px', color: '#666', fontWeight: 'normal' }}>{text}</span>;
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 500, flex: 1 }}>{text ?? "No data available"}</span>
            {!record.isDivided && (
              <Button
                type="text"
                size="small"
                icon={<SplitCellsOutlined />}
                onClick={() => openDividerModal(record)}
                style={{
                  color: '#1890ff',
                  padding: '2px 4px',
                  height: 'auto',
                  minWidth: 'auto'
                }}
                title="Divide data by property"
              />
            )}
            {record.isDivided && record.parentKey && (
              <Button
                type="text"
                size="small"
                onClick={() => handleUndoDivision(record.parentKey)}
                style={{
                  color: '#ff4d4f',
                  padding: '2px 4px',
                  height: 'auto',
                  minWidth: 'auto',
                  fontSize: '10px'
                }}
                title="Undo division"
              >
                ↶
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: "Source of data",
      dataIndex: "source",
      key: "source",
      width: 130,
      render: (text, record) => {
        // Handle instructions row
        if (record.isTotalRow) {
          return null
        }
        if (record.key === 'instructions') {
          return <span style={{ fontStyle: 'italic', fontSize: '12px', color: '#666' }}>{text}</span>;
        }

        return (
          <TextArea
            value={text || ""}
            onChange={(e) => handleInputChange(e.target.value, record.key, "source")}
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{ resize: 'none', cursor: 'pointer' }}
            placeholder="Click to open editor with suggestions..."
            onClick={() => openAutoCompleteModal(record, "source")}
            readOnly
          />
        );
      },
    },
    {
      title: "Verifier comments",
      dataIndex: "verifier",
      key: "verifier",
      width: 130,
      render: (text, record) => {

        if (record.isTotalRow) {
          return null
        }
        // Handle instructions row

        if (record.key === 'instructions') {
          return <span style={{ fontStyle: 'italic', fontSize: '12px', color: '#666' }}>{text}</span>;
        }

        return (
          <TextArea
            value={text || ""}
            onChange={(e) => handleInputChange(e.target.value, record.key, "verifier")}
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{ resize: 'none', cursor: 'pointer' }}
            placeholder="Click to open editor with suggestions..."
            onClick={() => openAutoCompleteModal(record, "verifier")}
            readOnly
          />
        );
      },
    },
    {
      title: "Total tCO₂e",
      dataIndex: "totalEmissions",
      key: "totalEmissions",
      width: 70,
      render: (text, record) => {
        if (record.key === 'instructions') {
          return <span style={{ fontStyle: 'italic', fontSize: '12px', color: '#666' }}>{text}</span>;
        }
        if (record.isTotalRow) {
          return <span style={{ fontWeight: 700 }}>{record.calculatedTotalEmissions?.toFixed(2)}</span>;
        }
        return <span style={{ fontWeight: 500 }}>{record.calculatedTotalEmissions?.toFixed(2)}</span>;
      },
    },
    {
      title: "Accuracy",
      dataIndex: "accuracy",
      key: "accuracy",
      width: 80,
      render: (text, record) => {
        if (record.isTotalRow) {
          return null
        }
        if (record.key === 'instructions') {
          return <span style={{ fontStyle: 'italic', fontSize: '12px', color: '#666' }}>{text}</span>;
        }
        return (
          <Select
            value={text || "n/a"}
            onChange={(value) => handleInputChange(value, record.key, "accuracy")}
            style={{ width: "100%" }}
            popupMatchSelectWidth={false}
            size="small"
            key={accuracyOptions.length}
            options={accuracyOptions.map(option => ({ label: option.label, value: option.value }))}
          />
        );
      },
    },
    {
      title: "Percentage",
      dataIndex: "percentage",
      key: "percentage",
      width: 60,
      render: (text, record) => {
        if (record.isTotalRow) {
          return null
        }
        if (record.key === 'instructions') {
          return <span style={{ fontStyle: 'italic', fontSize: '12px', color: '#666' }}>{text}</span>;
        }
        return <span>{record.calculatedPercentageDisplay}</span>;
      },
    },
    {
      title: "Materiality",
      dataIndex: "materiality",
      key: "materiality",
      width: 100,
      render: (text, record) => {
        if (record.isTotalRow) {
          return null
        }
        if (record.key === 'instructions') {
          return <span style={{ fontStyle: 'italic', fontSize: '12px', color: '#666' }}>{text}</span>;
        }
        return <span style={{ fontSize: '12px' }}>{record.calculatedMateriality}</span>;
      },
    },
    {
      title: "Uncertainty",
      dataIndex: "uncertainty",
      key: "uncertainty",
      width: 60,
      render: (text, record) => {
        if (record.isTotalRow) {
          return null
        }
        if (record.key === 'instructions') {
          return <span style={{ fontStyle: 'italic', fontSize: '12px', color: '#666' }}>{text}</span>;
        }
        return <span>{record.calculatedUncertaintyDisplay}</span>;
      },
    },
    {
      title: "Error Margin",
      dataIndex: "errorMargin",
      key: "errorMargin",
      width: 60,
      render: (text, record) => {
        if (record.isTotalRow) {
          return <span style={{ fontWeight: 700 }}>{record.calculatedErrorMarginDisplay}</span>;
        }
        if (record.key === 'instructions') {
          return <span style={{ fontStyle: 'italic', fontSize: '12px', color: '#666' }}>{text}</span>;
        }
        return <span>{record.calculatedErrorMarginDisplay}</span>;
      },
    },
  ], [optionsCache, handleInputChange, filterOption, renderDropdown, overallTotalEmissions, startDate, endDate, totalEmissionsForAllRows, totalErrorMargin]);

  // Instructions row data
  const instructionsRow = {
    key: 'instructions',
    data: 'Instructions',
    source: 'Add data source',
    verifier: 'Add comments',
    totalEmissions: 'Auto-calculated',
    accuracy: 'Select rating',
    percentage: 'Auto-calculated',
    materiality: 'Auto-calculated',
    uncertainty: 'Auto-calculated',
    errorMargin: 'Auto-calculated'
  };

  // Add instructions row and apply filtering and sorting to the computed data
  const td = useMemo(() => {
    // Filter the computed data
    const filteredData = computedTableData.filter(r => {
      if (r.isTotalRow) return true; // Always include totals row
      if (filters.hideZeroEmissions) {
        return r.calculatedTotalEmissions > 0;
      }
      return true;
    });

    // Sort the filtered data with grouping logic
    const sortedData = filteredData.sort((a, b) => {
      // Keep totals last
      if (a.isTotalRow) return 1;
      if (b.isTotalRow) return -1;

      // Extract parent names (everything before the first " - ")
      const aParent = a.data?.split(" - ")[0] || "";
      const bParent = b.data?.split(" - ")[0] || "";

      // First sort by parent name (alphabetically)
      if (aParent !== bParent) {
        // But prioritize by parent's total emissions first
        const aParentTotal = filteredData
          .filter(item => !item.isTotalRow && item.data?.split(" - ")[0] === aParent)
          .reduce((sum, item) => sum + (item.calculatedTotalEmissions || 0), 0);
        
        const bParentTotal = filteredData
          .filter(item => !item.isTotalRow && item.data?.split(" - ")[0] === bParent)
          .reduce((sum, item) => sum + (item.calculatedTotalEmissions || 0), 0);
        
        return bParentTotal - aParentTotal; // Higher emissions first
      }

      // Within the same parent group, sort by individual emissions (highest first)
      const aTotal = a.calculatedTotalEmissions || 0;
      const bTotal = b.calculatedTotalEmissions || 0;
      return bTotal - aTotal;
    });

    return [instructionsRow, ...sortedData];
  }, [computedTableData, filters.hideZeroEmissions]);

  // Handle opening divider modal
  const openDividerModal = useCallback((record) => {
    const formName = record.formName || record.data;
    const results = store.getState().app.results.filter(result => result.name === formName);

    if (results.length === 0) {
      Modal.info({
        title: "Cannot Divide",
        content: "Sorry, you cannot divide this data. No results available for analysis.",
      });
      return;
    }

    // Get divide options from attribute.js
    const divideOptions = getDivideByOptions(formName);

    if (divideOptions.length === 0) {
      Modal.info({
        title: "Cannot Divide",
        content: "Sorry, you cannot divide this data. No filterable properties available.",
      });
      return;
    }

    // Check each property for unique values and mark as disabled if needed
    const propertiesWithStatus = divideOptions.map(option => {
      const uniqueValues = [...new Set(results.map(result => result[option.value]).filter(val => val !== null && val !== undefined && val !== ""))];
      const canDivide = uniqueValues.length > 1;
      
      return {
        ...option,
        canDivide,
        uniqueValuesCount: uniqueValues.length,
        reason: !canDivide ? (uniqueValues.length === 0 ? "No data available" : "Only one unique value") : null
      };
    });

    setCurrentDividerRecord(record);
    setAvailableProperties(propertiesWithStatus);
    setSelectedProperty("");
    setDividerModalVisible(true);
  }, []);

  // Handle divider modal confirmation
  const handleDividerConfirm = useCallback(() => {
    if (!currentDividerRecord || !selectedProperty) return;

    // Find the selected property info to check if it's dividable
    const selectedPropertyInfo = availableProperties.find(prop => prop.value === selectedProperty);
    if (!selectedPropertyInfo || !selectedPropertyInfo.canDivide) {
      Modal.error({
        title: "Cannot Divide",
        content: `Cannot divide by this property: ${selectedPropertyInfo?.reason || "Unknown reason"}`,
      });
      return;
    }

    const formName = currentDividerRecord.formName || currentDividerRecord.data;
    const results = store.getState().app.results.filter(result => result.name === formName);

    // Get unique values for the selected property using the value (name) field
    const uniqueValues = [...new Set(results.map(result => result[selectedProperty]).filter(val => val !== null && val !== undefined && val !== ""))];

    // Create divided records
    const recordKey = currentDividerRecord.key;
    const dividedData = uniqueValues.map((value, index) => ({
      ...currentDividerRecord,
      key: `${recordKey}_${index}`,
      data: `${currentDividerRecord.data} - ${value}`,
      filterer: { property: selectedProperty, value: value },
      isDivided: true,
      parentKey: recordKey
    }));

    // Update the data source
    setTempDataSource(prevData => {
      const newData = prevData.filter(item => item.key !== recordKey);
      return [...newData, ...dividedData];
    });

    // Store divided records info
    setDividedRecords(prev => ({
      ...prev,
      [recordKey]: {
        property: selectedProperty,
        values: uniqueValues,
        originalRecord: currentDividerRecord
      }
    }));

    setDividerModalVisible(false);
    setCurrentDividerRecord(null);
    setSelectedProperty("");
  }, [currentDividerRecord, selectedProperty, availableProperties]);

  // Handle undoing division
  const handleUndoDivision = useCallback((parentKey) => {
    const divisionInfo = dividedRecords[parentKey];
    if (!divisionInfo) return;

    // Remove divided records and restore original
    setTempDataSource(prevData => {
      const filteredData = prevData.filter(item => item.parentKey !== parentKey);
      return [...filteredData, divisionInfo.originalRecord];
    });

    // Remove from divided records
    setDividedRecords(prev => {
      const newDivided = { ...prev };
      delete newDivided[parentKey];
      return newDivided;
    });
  }, [dividedRecords]);

  const modalTitle = useMemo(() => {
    return `Edit ${currentEditingColumn === 'source' ? 'Source of Data' : 'Verifier Comments'} - ${currentEditingField?.data || ''}`;
  }, [currentEditingColumn, currentEditingField]);

  // console.log(td, "tableDataWithInstructionsAndTotals")
  return (
    <div style={{ width: "100%", margin: 0, padding: 0 }}>
      <Visual storageKey="comments-table-visual">
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography.Text strong>Settings</Typography.Text>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{marginRight: 30}}
              onClick={handleAddAccuracyOption}
              size="small"
            >
              Add Accuracy Option
            </Button>
          </div>
          {<Checkbox checked={filters.hideZeroEmissions} onChange={(e) => setFilters({ ...filters, hideZeroEmissions: e.target.checked })}>Hide Zero Emissions</Checkbox>}
        </div>
        <Table
          dataSource={td}
          columns={columns}
          pagination={false}
          size="small"
          style={{ width: "100%", margin: 0, padding: 0 }}
          scroll={{ x: 1400, y: 'calc(100vh - 200px)' }}
          rowClassName={(record) => {
            if (record.key === 'instructions') return 'comments-table-instructions-row';
            if (record.isTotalRow) return 'comments-table-totals-row';
            return '';
          }}
        />
      </Visual>

      <style dangerouslySetInnerHTML={{
        __html: `
          .comments-table-instructions-row td {
            background-color: #fafafa !important;
            border-bottom: 2px solid #e8e8e8 !important;
          }
          .comments-table-instructions-row .ant-select {
            pointer-events: none;
          }
          .comments-table-instructions-row .ant-input,
          .comments-table-instructions-row .ant-input:focus,
          .comments-table-instructions-row .ant-input:hover {
            background-color: transparent !important;
            border: none !important;
            box-shadow: none !important;
            pointer-events: none;
          }
          .comments-table-totals-row td {
            background-color: #f5f5f5 !important;
            font-weight: bold !important;
            padding: 4px 8px !important;
            position: sticky !important;
            bottom: 0 !important;
            z-index: 2 !important;
          }
          .comments-table-totals-row .ant-input,
          .comments-table-totals-row .ant-select {
            pointer-events: none;
            background-color: transparent !important;
            border: none !important;
          }
          .ant-table-thead > tr > th {
            position: sticky !important;
            top: 0 !important;
            z-index: 2 !important;
            background: #fafafa !important;
            padding: 4px 8px !important;
          }
          .ant-table-tbody > tr > td {
            padding: 4px 8px !important;
          }
          .ant-table-cell {
            font-size: 12px !important;
          }
          .ant-table-cell[title="Data"] {
            font-size: 11px !important;
          }
        `
      }} />

      {/* Modal for adding new options */}
      <Modal
        title={
          <div style={{ fontSize: "16px", fontWeight: "600" }}>
            ➕ Add New Option
          </div>
        }
        open={isModalVisible}
        onOk={handleAddNewOption}
        onCancel={() => {
          setIsModalVisible(false);
          setNewOption("");
        }}
        okButtonProps={{
          disabled: !newOption.trim(),
          style: { fontWeight: "500" }
        }}
        cancelButtonProps={{
          style: { fontWeight: "500" }
        }}
        centered
        width={500}
      >
        <div style={{ marginBottom: "16px" }}>
          <div style={{ marginBottom: "8px", fontSize: "14px", color: "#666" }}>
            <strong>Field:</strong> {selectedDataField}
          </div>
          <div style={{ marginBottom: "16px", fontSize: "14px", color: "#666" }}>
            <strong>Column:</strong> {columnToUpdate === 'source' ? 'Source of data' : 'Verifier comments'}
          </div>
        </div>
        <Input.TextArea
          placeholder={`Enter new ${columnToUpdate === 'source' ? 'source' : 'verifier comment'} option...`}
          value={newOption}
          onChange={(e) => setNewOption(e.target.value)}
          autoSize={{ minRows: 3, maxRows: 6 }}
          style={{ fontSize: "14px" }}
        />
      </Modal>

      {/* AutoComplete Modal */}
      <AutoCompleteModal
        key={modalTitle}
        visible={autoCompleteModalVisible}
        onCancel={() => setAutoCompleteModalVisible(false)}
        onSave={handleAutoCompleteSave}
        title={modalTitle}
        value={currentEditingValue}
        suggestions={
          currentEditingField && currentEditingColumn
            ? (optionsCache[`${currentEditingField.formName || currentEditingField.data}_commenttable`] || defaultConfig)[currentEditingColumn]?.suggestions || []
            : []
        }
        placeholder={`Enter ${currentEditingColumn === 'source' ? 'source information' : 'verifier comments'}...`}
        onAddSuggestion={handleAddSuggestionFromModal}
        onRemoveSuggestion={handleRemoveSuggestionFromModal}
        fieldKey={currentEditingColumn}
      />

      {/* Divider Modal */}
      <Modal
        title={
          <div style={{ fontSize: "16px", fontWeight: "600" }}>
            🔀 Divide Data by Property
          </div>
        }
        open={dividerModalVisible}
        onOk={handleDividerConfirm}
        onCancel={() => {
          setDividerModalVisible(false);
          setCurrentDividerRecord(null);
          setSelectedProperty("");
        }}
        okButtonProps={{
          disabled: !selectedProperty,
          style: { fontWeight: "500" }
        }}
        cancelButtonProps={{
          style: { fontWeight: "500" }
        }}
        centered
        width={600}
      >
        <div style={{ marginBottom: "16px" }}>
          <div style={{ marginBottom: "8px", fontSize: "14px", color: "#666" }}>
            <strong>Data:</strong> {currentDividerRecord?.data}
          </div>
          <div style={{ marginBottom: "16px", fontSize: "14px", color: "#666" }}>
            Select a property to divide this data by. This will create separate rows for each unique value of the selected property.
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
            Available Properties:
          </label>
          <Select
            placeholder="Select a property to divide by..."
            value={selectedProperty}
            onChange={setSelectedProperty}
            style={{ width: "100%" }}
            size="large"
          >
            {availableProperties.map(property => (
              <Select.Option 
                key={property.value} 
                value={property.value}
                disabled={!property.canDivide}
                style={{
                  color: property.canDivide ? 'inherit' : '#ff4d4f',
                  opacity: property.canDivide ? 1 : 0.6
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{property.key}</span>
                  {!property.canDivide && (
                    <span style={{ 
                      fontSize: '12px', 
                      color: '#ff4d4f', 
                      fontStyle: 'italic',
                      marginLeft: '8px'
                    }}>
                      ({property.reason})
                    </span>
                  )}
                  {property.canDivide && (
                    <span style={{ 
                      fontSize: '12px', 
                      color: '#52c41a', 
                      fontStyle: 'italic',
                      marginLeft: '8px'
                    }}>
                      ({property.uniqueValuesCount} values)
                    </span>
                  )}
                </div>
              </Select.Option>
            ))}
          </Select>
        </div>

        {selectedProperty && (
          <div style={{
            padding: "12px",
            backgroundColor: "#f6f8fa",
            borderRadius: "6px",
            fontSize: "12px",
            color: "#666"
          }}>
            <strong>Preview:</strong> This will create separate rows like "{currentDividerRecord?.data} - {selectedProperty?.key} Value 1", "{currentDividerRecord?.data} - {selectedProperty?.key} Value 2", etc.
          </div>
        )}
      </Modal>

      
    </div>
  );
});

SimpleEditableTable.displayName = 'SimpleEditableTable';

export default SimpleEditableTable;
